import React, { useEffect, useState } from "react";
import {
  FileEntity,
  FileStatus,
} from "@quantizr-engineering/common/types/file";
import { useFileService } from "../api/fileService";
import FileStatusCell from "./FileStatusCell";
import Tooltip from "./Tooltip";

interface FileProcessingStatusModalProps {
  onClose: () => void;
  files: Partial<FileEntity>[];
}

const FileProcessingStatusModal: React.FC<FileProcessingStatusModalProps> = ({
  onClose,
  files: initialFiles,
}) => {
  const [files, setFiles] = useState(initialFiles);
  const [progress, setProgress] = useState(0);
  const { pollFileProcessingStatus } = useFileService();

  const calculateProgress = (currentFiles: typeof files) => {
    const completedFiles = currentFiles.filter(
      (f) => f.status === FileStatus.PROCESSED || f.status === FileStatus.ERROR
    ).length;
    return Math.round((completedFiles / currentFiles.length) * 100);
  };

  useEffect(() => {
    setFiles(initialFiles);
  }, [initialFiles]);

  useEffect(() => {
    const pollIntervals: NodeJS.Timeout[] = [];

    files.forEach((file) => {
      const pollInterval = setInterval(async () => {
        if (!file.id) return;
        try {
          const status = await pollFileProcessingStatus(file.id.toString());

          setFiles((currentFiles) => {
            const updatedFiles = currentFiles.map((f) =>
              f.id === file.id ? { ...f, status: status.status } : f
            );
            const newProgress = calculateProgress(updatedFiles);
            setProgress(newProgress);

            const allProcessed = updatedFiles.every(
              (f) => f.status === FileStatus.PROCESSED
            );

            if (allProcessed) {
              pollIntervals.forEach((interval) => clearInterval(interval));
              setTimeout(() => {
                setFiles([]);
                onClose();
              }, 3000);
            }

            return updatedFiles;
          });

          if (status.status === FileStatus.PROCESSED) {
            clearInterval(pollInterval);
          }
        } catch (error) {
          clearInterval(pollInterval);
        }
      }, 1000);

      pollIntervals.push(pollInterval);
    });

    return () => pollIntervals.forEach((interval) => clearInterval(interval));
  }, [initialFiles, onClose]);

  const handleClose = () => {
    setFiles([]);
    onClose();
  };

  const ProgressBar = ({ progress }: { progress: number }) => (
    <div className="mb-4">
      <div className="bg-gray-200 rounded-full h-2.5">
        <div
          className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-in-out"
          style={{ width: `${progress}%` }}
        />
      </div>
      <p className="text-sm text-gray-600 mt-1">progress: {progress}%</p>
    </div>
  );

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="modal-box relative bg-gray-100 z-50 max-w-4xl mx-auto p-4 max-h-[80vh] overflow-y-auto transform-gpu">
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 text-black font-bold hover:text-gray-600"
        >
          X
        </button>
        <div className="mb-4">
          <div className="bg-gray-100 p-4">
            <h3 className="text-lg font-semibold mb-3">Processing Files</h3>
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      File Name
                    </th>
                    <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {files.map((file) => (
                    <tr key={file.id}>
                      <td className="px-6 py-4">
                        <Tooltip
                          content={file.name ? file.name : file.filename}
                          display={
                            (file.name ? file.name : file.filename ?? "")
                              .length > 50
                          }
                        >
                          <p className="font-medium truncate max-w-96">
                            {file.name ? file.name : file.filename}
                          </p>
                        </Tooltip>
                      </td>
                      <FileStatusCell status={file.status} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <ProgressBar progress={progress} />
      </div>
    </div>
  );
};

export default FileProcessingStatusModal;
